import { fromZonedTime } from 'date-fns-tz';
import { reviewWorkDeclaredStatuses } from '@app/workflow-dashboards/workflow-review-work';
import { UiButtonDirective } from '@template-ui/directives';
import type {
  DetailConfigureTarget,
  DetailFieldOrder,
  DetailBlockMove,
} from '@app/workflow-workspace/workflow-workspace-authoring';
import { WorkflowLedger } from '@app/shared/components/workflow-ledger/workflow-ledger.models';
import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  AfterViewInit,
  OnDestroy,
  NgZone,
  Optional,
  ViewChild,
  ElementRef,
  TemplateRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { StepAuthoringCatalog } from './workflow-step-composition';
import { sameCapabilityReference } from './workflow-capability-authoring';
import {
  applyRequirementDetailDefaults,
  requirementPreviewChecklist,
} from './workflow-requirement-detail-defaults';
import type { WorkflowCompletionChecklist } from '@app/shared/workflow-commands/workflow-completion-requirements';
import { captureFormValues } from '@app/shared/workflow-commands/workflow-capture-form';
import { GeneratedCommandFormComponent } from '@app/shared/forms/generated-command-form.component';
import {
  request2LocalLookupSample,
  type Request2LocalLookupSample,
} from './workflow-request2-local-lookup-sample';
import {
  detailSampleJourney,
  sampleActionMatches,
  type DetailSampleJourney,
  type DetailSampleRequirement,
} from './workflow-detail-sample-journey';
import { WorkflowDashboardComponent } from '@app/workflow-dashboards/workflow-dashboard.component';
import {
  DashboardConfigureTarget,
  resolveDashboardPresentation,
  dashboardStatusChoices,
  dashboardBusinessStatusChoices,
  dashboardStepProgressChoices,
  dashboardAssignmentChoices,
  hasDashboardAssignment,
} from '@app/workflow-dashboards/workflow-dashboard-presentation';
import { WorkflowStepQueue } from '@app/workflow-dashboards/workflow-dashboards.service';
import {
  WorkflowWorkspace,
  WorkflowWorkspaceComponent,
  WorkspaceAction,
  WorkflowPreviewInputChange,
} from '@app/workflow-workspace/workflow-workspace.component';
import {
  validateGeneratedForm,
  type FormIssues,
  type GeneratedFormValues,
} from '@template-ui/helpers/generated-form.helpers';
import {
  WorkflowDefinitionDocument,
  WorkflowDefinitionNode,
  WorkflowCapabilityReference,
} from './platform-workflow-definition-lifecycle.models';

interface LocalRequestExampleView {
  sample: Request2LocalLookupSample;
  generation: number;
}

/** Synthetic presentation data only. Both actual staff renderers disable reads and writes in preview mode. */
@Component({
  selector: 'phms-workflow-presentation-preview',
  standalone: true,
  imports: [
    UiButtonDirective,
    CommonModule,
    WorkflowDashboardComponent,
    WorkflowWorkspaceComponent,
    GeneratedCommandFormComponent,
  ],
  template: `<div
    #canvas
    class="presentation-preview"
    [class.presentation-preview--phone]="canvasSize === 'phone'"
  >
    <p *ngIf="scope === 'dashboard' && dashboardNotice" role="status">
      {{ dashboardNotice }}
    </p>
    <p *ngIf="scope === 'detail' && detailNotice" role="status">
      {{ detailNotice }}
    </p>
    <p
      *ngIf="scope === 'dashboard' && assignmentExample && (authoring || dashboard?.dashboard?.kind !== 'preparation')"
      class="sample-boundary"
    >
      “Assigned to me” uses the example staff member in this synthetic preview.
    </p>
    <p
      *ngIf="scope === 'dashboard' && !authoring && dashboard?.dashboard?.kind === 'preparation'"
      class="sample-boundary"
      data-dashboard-preview-actor
    >
      Example staff member · synthetic actor 1. This example is separate from the
      signed-in author; “Assigned to me” uses this example. Record opening,
      assignment and staff operations are disabled.
    </p>
    <phms-workflow-dashboard
      *ngIf="scope === 'dashboard' && dashboard"
      [previewMode]="true"
      [previewQueue]="dashboard"
      [previewActorUserId]="syntheticActorUserId"
      [compact]="compact"
      [authoring]="authoring"
      [editingDisabled]="dashboardEditingDisabled"
      [summaryTools]="dashboardSummaryTools"
      [filterTools]="dashboardFilterTools"
      [columnTools]="dashboardColumnTools"
      [selectedTarget]="selectedTarget"
      (configure)="configure.emit($event)"
      (columnOrderChange)="columnOrderChange.emit($event)"
    />
    <phms-workflow-workspace
      *ngIf="scope === 'detail' && workspace"
      [previewMode]="true"
      [previewData]="workspace"
      [previewLedger]="workspaceLedger"
      [previewChecklist]="workspaceChecklist"
      [previewInputs]="sampleInputs"
      [previewReadOnly]="sampleReadOnly"
      (previewInputChange)="sampleInputChange.emit($event)"
      [detailAuthoring]="detailAuthoring"
      [detailEditingDisabled]="detailEditingDisabled"
      [detailSelection]="detailSelection"
      [detailFieldIssues]="detailFieldIssues"
      (detailConfigure)="detailConfigure.emit($event)"
      (detailFieldOrderChange)="detailFieldOrderChange.emit($event)"
      (detailBlockMove)="detailBlockMove.emit($event)"
      (detailTabChange)="detailTabChange.emit($event)"
    />
    <details *ngIf="showSampleJourney" class="sample-tools">
      <summary>
        <strong>{{
          sampleJourney.mode === 'step'
            ? 'Sample visit checks'
            : 'Sample action checks'
        }}</strong>
        <span>Nothing is saved.</span>
      </summary>
      <section class="sample-journey" aria-label="Sample staff journey">
        <header>
          <div>
            <strong>{{
              sampleJourney.mode === 'step'
                ? 'Try a sample visit'
                : 'Try a sample action'
            }}</strong>
            <p>
              Try answers on the page, then check the sample outcome. Nothing is
              saved.
            </p>
          </div>
          <button uiButton type="button" (click)="resetSampleScenario()">
            Reset scenario
          </button>
        </header>
        <div
          *ngIf="sampleJourney.mode === 'step'"
          class="sample-scenarios"
          role="group"
          aria-label="Sample requirement state"
        >
          <span>Scenario</span>
          <button
            uiButton
            [text]="true"
            [outlined]="false"
            type="button"
            [attr.aria-pressed]="sampleScenario === 'missing'"
            (click)="selectSampleScenario('missing')"
          >
            Requirements missing
          </button>
          <button
            uiButton
            [text]="true"
            [outlined]="false"
            type="button"
            [attr.aria-pressed]="sampleScenario === 'supplied'"
            (click)="selectSampleScenario('supplied')"
          >
            Requirements supplied
          </button>
        </div>
        <p *ngIf="sampleScenario === 'supplied'">
          This scenario assumes the required work has been supplied. It creates
          no uploaded files or recorded decisions.
        </p>
        <ul
          class="sample-requirements"
          *ngIf="sampleJourney.requirements.length; else noSampleRequirements"
        >
          <li
            *ngFor="let requirement of sampleJourney.requirements"
            [attr.data-state]="sampleRequirementState(requirement)"
          >
            <div>
              <strong>{{ requirement.label }}</strong>
              <p>{{ requirement.unavailable || requirement.description }}</p>
            </div>
            <span class="sample-requirement-state">{{
              sampleRequirementLabel(requirement)
            }}</span>
            <button
              uiButton
              *ngIf="requirement.source && !requirement.unavailable"
              type="button"
              (click)="openSampleForm(requirement)"
            >
              Try form
            </button>
          </li>
        </ul>
        <ng-template #noSampleRequirements
          ><p>
            {{
              sampleJourney.mode === 'step'
                ? 'No completion requirements are configured for this step.'
                : 'This page uses its configured staff actions. Choose an action on the page to try its form and outcome.'
            }}
          </p></ng-template
        >
        <ul
          *ngIf="sampleJourney.configurationIssues.length"
          class="sample-configuration"
          aria-label="Sample configuration issues"
        >
          <li *ngFor="let issue of sampleJourney.configurationIssues">
            {{ sampleConfigurationMessage(issue) }}
          </li>
        </ul>
        <p class="sample-boundary">
          Live completion checks access, current-visit evidence and saved rules
          on the server.
        </p>
      </section>
      <section class="sample-checks" aria-label="Sample check controls">
        <section
          *ngFor="
            let example of localRequestExampleViews;
            trackBy: trackLocalRequestExample
          "
          class="sample-local-example"
          data-request2-local-example
          aria-label="Local Request review example"
        >
          <h4>Local Request review example</h4>
          <p>
            Artificial choices only. The saved lookup form stays read-only.
            Requester/reviewer independence is not evaluated.
          </p>
          <p class="sample-boundary">
            Saved lookup: {{ example.sample.configurationCode }} /
            {{ example.sample.domainCode }}
          </p>
          <phms-generated-command-form
            [definition]="example.sample.form"
            [values]="localRequestExampleValues"
            [issues]="localRequestExampleIssues"
            [disabled]="sampleReadOnly"
            (valuesChange)="changeLocalRequestExample(example, $event)"
          />
          <button
            uiButton
            type="button"
            [disabled]="sampleReadOnly"
            data-check-request2-local-example
            (click)="checkLocalRequestExample(example)"
          >
            Check local example
          </button>
          <p role="status" aria-live="polite" data-request2-local-feedback>
            {{ localRequestExampleFeedback }}
          </p>
        </section>
        <div class="sample-actions">
          <button
            uiButton
            *ngIf="sampleHasForms"
            type="button"
            (click)="checkSampleAnswers()"
          >
            {{ localRequestExampleView ? 'Check page answers' : 'Check sample answers' }}
          </button>
          <button
            uiButton
            severity="primary"
            [outlined]="false"
            type="button"
            (click)="checkSampleCompletion()"
          >
            {{
              sampleJourney.mode === 'step'
                ? 'Check sample completion'
                : localRequestExampleView ? 'Check page action' : 'Check sample action'
            }}
          </button>
        </div>
        <p class="sample-feedback" role="status" aria-live="polite">
          {{ sampleFeedback }}
        </p>
      </section>
    </details>
  </div>`,
  styles: [
    `
      :host {
        display: block;
        min-width: 0;
        width: 100%;
      }
      .presentation-preview {
        width: 100%;
        min-width: 0;
        margin-inline: auto;
      }
      .presentation-preview--phone {
        width: min(100%, 390px);
      }
      .sample-tools {
        container: workflow-sample-tools / inline-size;
        margin-top: 12px;
        border: 1px solid
          var(--draft-border, var(--custom-input-normal-border-color));
        border-radius: 4px;
        background: var(--custom-surface-panel-color);
        color: var(--custom-text-strong-color);
        font-size: 13px;
      }
      .sample-tools > summary {
        padding: 8px 10px;
        cursor: pointer;
      }
      .sample-tools > summary span {
        margin-inline-start: 8px;
        font-size: 12px;
        color: var(--draft-muted, var(--custom-text-normal-color));
      }
      .sample-tools > summary:focus-visible {
        outline: 2px solid var(--custom-focus-ring-color);
        outline-offset: 2px;
      }
      .sample-journey,
      .sample-checks {
        padding: 10px;
      }
      .sample-checks {
        border-top: 1px solid
          var(--draft-border, var(--custom-input-normal-border-color));
      }
      .sample-journey header,
      .sample-scenarios,
      .sample-actions {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 8px;
      }
      .sample-journey header {
        align-items: start;
        justify-content: space-between;
        gap: 12px;
      }
      .sample-journey p,
      .sample-checks p {
        margin: 4px 0 0;
        line-height: 1.45;
      }

      .sample-journey button[aria-pressed='true'] {
        color: var(--custom-interactive-selected-text-color);
        background: var(--custom-interactive-selected-background-color);
        border-color: var(--custom-interactive-selected-border-color);
      }

      .sample-scenarios {
        margin: 12px 0;
      }
      .sample-requirements {
        list-style: none;
        padding: 0;
        margin: 12px 0;
        display: grid;
        gap: 6px;
      }
      .sample-requirements li {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 9px 10px;
        border-left: 3px solid var(--custom-input-normal-border-color);
        background: var(--custom-input-background-color);
      }
      .sample-requirements li > div {
        flex: 1;
      }
      .sample-requirement-state {
        font-size: 12px;
        font-weight: 600;
      }
      .sample-feedback {
        padding: 8px 0;
        font-weight: 600;
      }
      .sample-boundary {
        font-size: 12px;
        color: var(--draft-muted, var(--custom-text-normal-color));
      }
      .sample-local-example {
        padding: 12px;
        margin-bottom: 12px;
        border: 1px solid var(--custom-input-normal-border-color);
        border-radius: 6px;
        overflow-wrap: anywhere;
      }
      .sample-local-example h4 {
        margin: 0 0 6px;
        font-size: 14px;
      }
      .sample-local-example phms-generated-command-form {
        margin-top: 12px;
      }
      @container workflow-sample-tools (max-width: 650px) {
        .sample-requirements li {
          flex-wrap: wrap;
        }
        .sample-requirements li > div {
          flex-basis: 100%;
        }
      }
    `,
  ],
})
export class WorkflowPresentationPreviewComponent
  implements OnChanges, AfterViewInit, OnDestroy
{
  @Input({ required: true }) document!: WorkflowDefinitionDocument;
  @Input({ required: true }) node!: WorkflowDefinitionNode;
  /** Identity exists only in these labelled examples, never in the application directory. */
  readonly syntheticActorUserId = 1;
  get assignmentExample(): boolean {
    return (
      !!this.dashboard?.presentation?.filters.some(
        (filter) => filter.field === 'assignment',
      ) ||
      !!this.dashboard?.presentation?.tiles.some(
        (tile) => tile.statusScope === 'assignment',
      )
    );
  }
  @Input() scope: 'dashboard' | 'detail' = 'dashboard';
  @Input() dashboardId = '';
  /** Reapply authored starting filters locally; never use the staff clear-filters action. */
  @Input() dashboardResetVersion = 0;
  private appliedDashboardResetVersion = 0;
  @Input() compact = false;
  /** Constrains the shared renderer's container without scaling its controls. */
  @Input() canvasSize: 'available' | 'phone' = 'available';
  @Output() canvasWidthChange = new EventEmitter<number>();
  @Input() authoring = false;
  @Input() dashboardEditingDisabled = false;
  @Input() dashboardSummaryTools: TemplateRef<unknown> | null = null;
  @Input() dashboardFilterTools: TemplateRef<unknown> | null = null;
  @Input() dashboardColumnTools: TemplateRef<unknown> | null = null;
  @Input() selectedTarget: DashboardConfigureTarget | null = null;
  @Output() configure = new EventEmitter<DashboardConfigureTarget>();
  @Output() columnOrderChange = new EventEmitter<string[]>();
  @Input() detailAuthoring = false;
  @Input() detailEditingDisabled = false;
  @Input() detailSampleJourney = false;
  @Input() sampleInputs: Record<string, GeneratedFormValues> | null = null;
  @Input() sampleReadOnly = false;
  @Input() sampleVisitNumber = 1;
  @Input() sampleVisitCompleted = false;
  @Output() sampleInputChange = new EventEmitter<WorkflowPreviewInputChange>();
  @Input() detailSelection: DetailConfigureTarget | null = null;
  @Input() detailFieldIssues: Record<string, string> = {};
  @Output() detailConfigure = new EventEmitter<DetailConfigureTarget>();
  @Output() detailFieldOrderChange = new EventEmitter<DetailFieldOrder>();
  @Output() detailBlockMove = new EventEmitter<DetailBlockMove>();
  @Output() detailTabChange = new EventEmitter<string>();
  private previewSignature = '';
  @ViewChild('canvas') private canvas?: ElementRef<HTMLElement>;
  private canvasObserver?: ResizeObserver;
  private lastCanvasWidth = 0;
  private destroyed = false;

  constructor(@Optional() private readonly zone?: NgZone) {}

  ngAfterViewInit(): void {
    const canvas = this.canvas?.nativeElement;
    if (!canvas) return;
    if (typeof ResizeObserver !== 'undefined') {
      this.canvasObserver = new ResizeObserver((entries) => {
        const width = entries.find((entry) => entry.target === canvas)
          ?.contentRect.width;
        if (width !== undefined) this.reportCanvasWidth(width);
      });
      this.canvasObserver.observe(canvas);
    }
    // Parent toolbars receive the measurement after their initial check.
    queueMicrotask(() =>
      this.reportCanvasWidth(canvas.getBoundingClientRect().width),
    );
  }
  private reportCanvasWidth(width: number): void {
    const measured = Math.round(width);
    if (this.destroyed || measured <= 0 || measured === this.lastCanvasWidth)
      return;
    this.lastCanvasWidth = measured;
    if (this.zone) this.zone.run(() => this.canvasWidthChange.emit(measured));
    else this.canvasWidthChange.emit(measured);
  }
  ngOnDestroy(): void {
    this.destroyed = true;
    this.canvasObserver?.disconnect();
  }
  @Input() componentCatalog: StepAuthoringCatalog | null = null;
  dashboardNotice = '';
  detailNotice = '';
  dashboard: WorkflowStepQueue | null = null;
  workspaceLedger: WorkflowLedger | null = null;
  workspaceChecklist: WorkflowCompletionChecklist | null = null;
  workspace: WorkflowWorkspace | null = null;
  private workspaceActions: WorkspaceAction[] = [];
  @ViewChild(WorkflowWorkspaceComponent)
  private detailRenderer?: WorkflowWorkspaceComponent;
  @ViewChild(WorkflowWorkspaceComponent, { read: ElementRef })
  private detailRendererElement?: ElementRef<HTMLElement>;
  sampleScenario: 'missing' | 'supplied' = 'missing';
  sampleFeedback = '';
  sampleJourney: DetailSampleJourney = {
    mode: 'action',
    requirements: [],
    configurationIssues: [],
    destination: null,
    actionBehaviors: [],
  };
  private readonly sampleFormChecks = new Map<string, string>();
  private localRequestExample: Request2LocalLookupSample | null = null;
  private localRequestExampleGeneration = 0;
  private localRequestExamplePosture = '';
  localRequestExampleView: LocalRequestExampleView | null = null;
  localRequestExampleViews: LocalRequestExampleView[] = [];
  localRequestExampleValues: GeneratedFormValues = {};
  localRequestExampleIssues: FormIssues = {};
  localRequestExampleFeedback = '';

  private readLocalRequestExample(): Request2LocalLookupSample | null {
    const attached =
      this.document?.nodes?.filter((node) => node?.id === this.node?.id) ?? [];
    if (
      attached.length !== 1 ||
      JSON.stringify(attached[0]) !== JSON.stringify(this.node)
    )
      return null;
    return request2LocalLookupSample(
      this.document,
      this.node.id,
      this.componentCatalog,
    );
  }
  private renewLocalRequestExampleView(): void {
    this.localRequestExampleGeneration++;
    this.localRequestExampleView = this.localRequestExample
      ? {
          sample: this.localRequestExample,
          generation: this.localRequestExampleGeneration,
        }
      : null;
    // A renewed lease remounts this local form, disconnecting delayed old control outputs.
    this.localRequestExampleViews = this.localRequestExampleView
      ? [this.localRequestExampleView]
      : [];
  }
  trackLocalRequestExample(
    _index: number,
    view: LocalRequestExampleView,
  ): number {
    return view.generation;
  }
  private localRequestExampleCurrent(view: LocalRequestExampleView): boolean {
    if (
      this.destroyed ||
      !this.showSampleJourney ||
      this.sampleReadOnly ||
      view !== this.localRequestExampleView ||
      view.generation !== this.localRequestExampleGeneration ||
      this.readLocalRequestExample()?.namespace !== view.sample.namespace
    )
      return false;
    const panel = this.canvas?.nativeElement.querySelector<HTMLElement>(
      '[data-request2-local-example]',
    );
    if (
      !panel?.isConnected ||
      panel.closest('[hidden],[inert],[aria-hidden="true"]') ||
      !panel.closest<HTMLDetailsElement>('details.sample-tools')?.open
    )
      return false;
    for (
      let element: HTMLElement | null = panel;
      element;
      element = element.parentElement
    ) {
      const style =
        element.ownerDocument.defaultView?.getComputedStyle(element);
      if (style?.display === 'none' || style?.visibility === 'hidden')
        return false;
    }
    return true;
  }
  changeLocalRequestExample(
    view: LocalRequestExampleView,
    values: GeneratedFormValues,
  ): void {
    if (
      !this.localRequestExampleCurrent(view) ||
      !values ||
      typeof values !== 'object' ||
      Array.isArray(values) ||
      Object.keys(values).some(
        (key) =>
          key !== 'workSummary' &&
          !(key === '__isProxy' && values[key] === true),
      )
    )
      return;
    const value = values['workSummary'];
    if (
      value != null &&
      value !== '' &&
      !view.sample.form.fields[0].options?.some(
        (option) => option.value === value,
      )
    )
      return;
    this.localRequestExampleValues =
      value == null || value === '' ? {} : { workSummary: value };
    this.localRequestExampleIssues = {};
    this.localRequestExampleFeedback = '';
  }
  checkLocalRequestExample(view: LocalRequestExampleView): boolean {
    if (!this.localRequestExampleCurrent(view)) return false;
    this.localRequestExampleIssues = validateGeneratedForm(
      view.sample.form,
      this.localRequestExampleValues,
    );
    if (Object.keys(this.localRequestExampleIssues).length) {
      this.localRequestExampleFeedback = 'Choose an artificial example value.';
      return false;
    }
    this.localRequestExampleFeedback =
      'This local example would be submitted; the step would stay open. No request or review is recorded.';
    return true;
  }
  sampleConfigurationMessage(issue: string): string {
    return issue === this.localRequestExample?.unavailableIssue
      ? 'The saved lookup form stays read-only here. Artificial local checks are available below.'
      : issue;
  }

  selectSampleAction(command: WorkflowCapabilityReference): void {
    const matches = this.workspaceActions.filter((action) =>
      sampleActionMatches(action.command, command),
    );
    if (matches.length === 1) this.detailRenderer?.choose(matches[0]);
  }

  get showSampleJourney(): boolean {
    return (
      this.detailSampleJourney &&
      this.scope === 'detail' &&
      !this.detailAuthoring &&
      !!this.workspace
    );
  }
  get sampleHasForms(): boolean {
    return this.workspaceActions.some((action) => !!action.form);
  }
  selectSampleScenario(scenario: 'missing' | 'supplied'): void {
    this.sampleScenario = scenario;
    this.sampleFeedback = '';
  }
  resetSampleScenario(): void {
    this.sampleFormChecks.clear();
    this.selectSampleScenario('missing');
    this.localRequestExampleValues = {};
    this.localRequestExampleIssues = {};
    this.localRequestExampleFeedback = '';
    this.renewLocalRequestExampleView();
  }
  sampleRequirementState(
    requirement: DetailSampleRequirement,
  ): 'unavailable' | 'supplied' | 'checked' | 'missing' {
    if (requirement.unavailable) return 'unavailable';
    if (this.sampleScenario === 'supplied') return 'supplied';
    if (!requirement.formId || !this.sampleFormChecks.has(requirement.formId))
      return 'missing';
    const values = requirement.source
      ? this.detailRenderer?.previewDraftValues(requirement.source.capability)
      : null;
    if (
      !values ||
      !requirement.formDefinition ||
      JSON.stringify(captureFormValues(requirement.formDefinition, values)) !==
        this.sampleFormChecks.get(requirement.formId)
    )
      return 'missing';
    return 'checked';
  }
  sampleRequirementLabel(requirement: DetailSampleRequirement): string {
    return {
      unavailable: 'Unavailable',
      supplied: 'Supplied in scenario',
      checked: 'Sample answers checked',
      missing: 'Missing',
    }[this.sampleRequirementState(requirement)];
  }
  openSampleForm(requirement: DetailSampleRequirement): void {
    if (
      !this.showSampleJourney ||
      !requirement.source ||
      requirement.unavailable
    )
      return;
    const matches = this.workspaceActions.filter((action) =>
      sampleActionMatches(action.command, requirement.source!.capability),
    );
    if (matches.length === 1) {
      this.detailRenderer?.choose(matches[0]);
      setTimeout(() => {
        const control =
          this.detailRendererElement?.nativeElement.querySelector<HTMLElement>(
            'phms-generated-command-form input:not([disabled]), phms-generated-command-form textarea:not([disabled]), phms-generated-command-form select:not([disabled])',
          );
        control?.focus({ preventScroll: true });
        (
          control ?? this.detailRendererElement?.nativeElement
        )?.scrollIntoView?.({ block: 'center' });
      });
    }
    this.sampleFeedback = '';
  }
  checkSampleAnswers(): boolean {
    if (!this.showSampleJourney) return false;
    const renderer = this.detailRenderer,
      action = renderer?.action(),
      form = renderer?.form();
    if (!renderer || !action || !form) {
      this.sampleFeedback =
        'Choose an action with a compatible form to check sample answers.';
      return false;
    }
    renderer.issues = renderer.clientIssues();
    const first = Object.entries(renderer.issues)[0];
    if (first) {
      this.sampleFeedback = `${form.fields.find((field) => field.name === first[0])?.label || first[0]}: ${first[1]}`;
      return false;
    }
    const formId = action.form?.captureForm?.id;
    if (
      formId &&
      this.sampleJourney.requirements.some(
        (requirement) =>
          requirement.formId === formId && !requirement.unavailable,
      )
    ) {
      this.sampleFormChecks.set(
        formId,
        JSON.stringify(captureFormValues(form, renderer.values)),
      );
    }
    this.sampleFeedback =
      this.sampleJourney.mode === 'step'
        ? 'Sample answers pass validation. The step stays open until the remaining requirements are supplied and completion is checked.'
        : 'Sample answers pass validation. Check the sample action to see its configured completion behavior.';
    return true;
  }
  checkSampleCompletion(): void {
    if (!this.showSampleJourney) return;
    const unavailable = this.sampleJourney.requirements.filter(
      (requirement) => !!requirement.unavailable,
    );
    if (
      this.sampleJourney.configurationIssues.length ||
      unavailable.length ||
      this.detailNotice
    ) {
      this.sampleFeedback = `Sample ${this.sampleJourney.mode === 'step' ? 'completion' : 'action'} is unavailable. Resolve the page configuration issues first.`;
      return;
    }
    if (this.sampleJourney.mode === 'action') {
      this.checkLegacySampleAction();
      return;
    }
    const missing = this.sampleJourney.requirements.filter(
      (requirement) => this.sampleRequirementState(requirement) === 'missing',
    );
    if (missing.length) {
      this.sampleFeedback = `Still missing in this sample visit: ${missing.map((requirement) => requirement.label).join(', ')}.`;
      return;
    }
    const next = this.sampleJourney.destination;
    this.sampleFeedback = next?.terminal
      ? 'Sample outcome: this step would complete the workflow.'
      : next
        ? `Sample outcome: this step would complete, then open “${next.label}”.`
        : 'Sample outcome: the requirements are supplied. The live workflow determines the next destination.';
  }
  private checkLegacySampleAction(): void {
    const action = this.detailRenderer?.action() ?? this.workspaceActions[0];
    const behavior = action
      ? this.sampleJourney.actionBehaviors.find((item) =>
          sampleActionMatches(action.command, item.command),
        )
      : null;
    if (!behavior) {
      this.sampleFeedback =
        'Choose a configured staff action to check its sample outcome.';
      return;
    }
    if (behavior.requiresForm && !this.checkSampleAnswers()) return;
    if (behavior.completesStep === false) {
      this.sampleFeedback = `Sample outcome: “${behavior.label}” would be submitted; this step would stay open.`;
      return;
    }
    const result = behavior.outcomes.find(
      (outcome) =>
        outcome.code ===
        (behavior.outcomeField
          ? this.detailRenderer?.values[behavior.outcomeField]
          : undefined),
    );
    const targets = result
      ? this.document.nodes.filter(
          (node) => node.id === result.destinationNodeId,
        )
      : [];
    const target =
      targets.length === 1 &&
      ['human_step', 'terminal'].includes(targets[0].typeCode)
        ? targets[0]
        : null;
    this.sampleFeedback =
      behavior.completesStep === true
        ? target?.typeCode === 'human_step'
          ? `Sample outcome: “${behavior.label}” would finish this step, then open “${target.displayName || target.stepCode || target.id}”.`
          : `Sample outcome: “${behavior.label}” would finish this step. The live workflow applies its configured destination.`
        : `Sample outcome: “${behavior.label}” would be submitted. The live workflow determines step completion.`;
  }
  private reviewWorkExample(status: string, index: number) {
    const sourceId = index + 1;
    const sameVisit = status === 'pending' || index % 3 === 2;
    const available = status === 'pending' || index % 3 !== 0;
    const targetId = sameVisit ? sourceId : index + 101;
    const sourceRoute = `/workflows/${sourceId}/steps/${sourceId}`;
    const route = available ? `/workflows/${sourceId}/steps/${targetId}` : '';
    return {
      decisionRecordId: 100 + index,
      sourceRoute,
      route,
      isCurrent: sameVisit,
      stepProgress: sameVisit ? 'in_progress' : 'completed',
      workPosture:
        status === 'pending'
          ? ('pending_review' as const)
          : index % 3 === 1
            ? ('returned_correction' as const)
            : index % 3 === 2
              ? ('returned_review' as const)
              : ('unavailable' as const),
      currentWork: available
        ? {
            workflowInstanceId: sourceId,
            occurrenceId: targetId,
            occurrenceNumber: sameVisit ? 1 : 2,
            nodeId: sameVisit ? this.node.id : 'synthetic-correction',
            stepCode: sameVisit
              ? this.node.stepCode || this.node.id
              : 'synthetic-correction',
            stepLabel: sameVisit
              ? this.node.displayName || this.node.stepCode || this.node.id
              : 'Example correction step',
            route,
          }
        : null,
      unavailableReasonCode: available
        ? null
        : 'workflow.review_work.current_unavailable',
    };
  }
  ngOnChanges(): void {
    const localPosture = JSON.stringify([
      this.scope,
      this.detailSampleJourney,
      this.detailAuthoring,
      this.sampleReadOnly,
    ]);
    if (localPosture !== this.localRequestExamplePosture) {
      this.localRequestExamplePosture = localPosture;
      this.renewLocalRequestExampleView();
    }
    const reset =
      this.appliedDashboardResetVersion !== this.dashboardResetVersion;
    this.appliedDashboardResetVersion = this.dashboardResetVersion;
    const signature = JSON.stringify([
      this.document,
      this.node,
      this.dashboardId,
      this.componentCatalog,
      this.sampleVisitNumber,
      this.sampleVisitCompleted,
    ]);
    if (signature === this.previewSignature) {
      if (
        reset &&
        this.scope === 'dashboard' &&
        !this.authoring &&
        this.dashboard?.dashboard.kind === 'preparation'
      )
        this.dashboard = structuredClone(this.dashboard);
      return;
    }
    this.previewSignature = signature;
    this.localRequestExample = this.readLocalRequestExample();
    this.resetSampleScenario();
    this.sampleJourney = {
      mode: 'action',
      requirements: [],
      configurationIssues: [],
      destination: null,
      actionBehaviors: [],
    };
    const draftDocument = this.document.nodes.includes(this.node)
      ? this.document
      : {
          ...this.document,
          nodes: this.document.nodes.map((node) =>
            node.id === this.node.id ? this.node : node,
          ),
        };
    const pageDocument = applyRequirementDetailDefaults(
      draftDocument,
      this.node.id,
      this.componentCatalog,
    );
    const pageNodes = pageDocument.nodes.filter(
      (node) => node.id === this.node.id,
    );
    const pageNode = pageNodes.length === 1 ? pageNodes[0] : this.node;
    const declaration = pageNode.declaration;
    this.dashboard = null;
    this.workspace = null;
    this.workspaceActions = [];
    this.workspaceLedger = null;
    this.workspaceChecklist = null;
    this.dashboardNotice = '';
    this.detailNotice = '';
    if (!declaration) return;
    const view =
      declaration.dashboards?.find((d) => d.id === this.dashboardId) ??
      declaration.dashboards?.[0];
    const registeredViews =
      this.componentCatalog?.views.filter(
        (item) => view && sameCapabilityReference(item.capability, view.view),
      ) ?? [];
    const registeredView =
      registeredViews.length === 1 ? registeredViews[0] : null;
    const timers = declaration.behavior?.timers;
    if (
      this.scope === 'dashboard' &&
      view?.view?.key?.provider === 'platform' &&
      view.view.key.code === 'workflow.preparation.work' &&
      (!/^1\.(?:[0-9]|1[0-9]|2[01])$/.test(this.document.payloadSchemaVersion) ||
        (timers != null &&
          (!Array.isArray(timers) ||
            timers.some(
              (timer) =>
                !timer ||
                typeof timer !== 'object' ||
                Array.isArray(timer) ||
                !('version' in timer) ||
                timer.version !== 1,
            ))))
    ) {
      this.dashboardNotice =
        'This saved timer or document format has no supported synthetic example. Its original settings are retained.';
      return;
    }
    if (
      view &&
      registeredView &&
      view.view.key.kind === 'view' &&
      view.view.key.version === 1 &&
      ((view.view.key.provider === 'platform' &&
        [
          'workflow.review.records',
          'workflow.review.work',
          'workflow.preparation.work',
        ].includes(view.view.key.code)) ||
        (view.view.key.provider === 'hearings' &&
          view.view.key.code === 'hearings.day-work'))
    ) {
      const hearingDay = view.view.key.code === 'hearings.day-work';
      const reviewWork = view.view.key.code === 'workflow.review.work';
      const declaredReviewStatuses = reviewWorkDeclaredStatuses(
        view.defaults['statuses'],
      );
      const preparation =
        view.view.key.code === 'workflow.preparation.work' || hearingDay;
      const sampleDay = String(view.defaults['day'] || '2026-10-04');
      const sampleZone = String(view.defaults['timeZoneId'] || 'UTC');
      const sampleStart = fromZonedTime(`${sampleDay}T09:00:00`, sampleZone);
      const sampleEnd = fromZonedTime(`${sampleDay}T10:00:00`, sampleZone);
      const presentation = resolveDashboardPresentation(
        view,
        registeredView,
        this.document,
        this.node.id,
      );
      const policies =
        this.document.assignmentPolicies?.filter(
          (policy) =>
            policy?.scopeCode === 'step' && policy?.nodeId === this.node.id,
        ) ?? [];
      const sampleQueue =
        policies.length === 1 ? policies[0]!.initialQueueCode || null : null;
      const initial = (field: string) =>
        presentation.filters.find((filter) => filter.field === field)
          ?.initialValue || '';
      const statusOptions = dashboardStatusChoices(registeredView).map(
        (choice) => choice.code,
      );
      const workflowStatusOptions = dashboardBusinessStatusChoices(
        this.document,
        this.node.id,
        'workflow',
      ).filter(
        (choice) =>
          !view.workflowStatusCodes?.length ||
          view.workflowStatusCodes.includes(choice.code),
      );
      const stepStatusOptions = dashboardBusinessStatusChoices(
        this.document,
        this.node.id,
        'step',
      ).filter(
        (choice) =>
          !view.stepStatusCodes?.length ||
          view.stepStatusCodes.includes(choice.code),
      );
      const standardProgress = [
        '1.12',
        '1.13',
        '1.14',
        '1.15',
        '1.16',
        '1.17', '1.18', '1.19', '1.20', '1.21',
      ].includes(this.document.payloadSchemaVersion);
      const progressChoices = dashboardStepProgressChoices.filter(
        (choice) =>
          choice.code !== 'bypassed' ||
          this.node.stepProgressConfiguration?.bypass,
      );
      const baseRows = (
        reviewWork
          ? ['pending', 'returned', 'returned', 'returned'].map(
              (statusCode) => ({
                decisionRecordId: 100 + (statusCode === 'pending' ? 1 : 2),
                statusCode,
                notes: null,
                workSummary: null,
              }),
            )
          : hearingDay
            ? ['pending', 'assigned', 'in_progress'].map((statusCode) => ({
                decisionRecordId: null,
                statusCode,
                queueCode: null,
                notes: null,
                workSummary: null,
              }))
            : preparation
              ? standardProgress
                ? progressChoices.map((choice) => ({
                    decisionRecordId: null,
                    statusCode: choice.code,
                    stepProgress: choice.code,
                    queueCode: sampleQueue,
                    notes: null,
                    workSummary: null,
                  }))
                : [
                    {
                      decisionRecordId: null,
                      statusCode: 'in_progress',
                      queueCode: sampleQueue,
                      notes: null,
                      workSummary: null,
                    },
                    {
                      decisionRecordId: null,
                      statusCode: 'completed',
                      queueCode: null,
                      notes: null,
                      workSummary: null,
                    },
                  ]
              : [
                  {
                    decisionRecordId: null,
                    statusCode: 'not_requested',
                    notes: null,
                    workSummary: null,
                  },
                  {
                    decisionRecordId: 101,
                    statusCode: 'pending',
                    notes: null,
                    workSummary: null,
                  },
                  {
                    decisionRecordId: 102,
                    statusCode: 'returned',
                    notes: null,
                    workSummary: null,
                  },
                  {
                    decisionRecordId: 103,
                    statusCode: 'approved',
                    notes: null,
                    workSummary: null,
                  },
                ]
      ).filter((r) =>
        reviewWork
          ? declaredReviewStatuses.includes(r.statusCode)
          : !view.defaults['status'] ||
            r.statusCode === view.defaults['status'],
      );
      // Cover each authored label without multiplying every pair into a large sample grid.
      const sampleStatuses = Array.from(
        {
          length: Math.max(
            workflowStatusOptions.length,
            stepStatusOptions.length,
            1,
          ),
        },
        (_, index) => ({
          workflowBusinessStatus:
            workflowStatusOptions[index % workflowStatusOptions.length] ?? null,
          stepBusinessStatus:
            stepStatusOptions[index % stepStatusOptions.length] ?? null,
        }),
      );
      const initialWorkflowStatus = workflowStatusOptions.find(
        (choice) => choice.code === initial('workflowStatus'),
      );
      const initialStepStatus = stepStatusOptions.find(
        (choice) => choice.code === initial('stepStatus'),
      );
      if (
        initialWorkflowStatus &&
        initialStepStatus &&
        !sampleStatuses.some(
          (sample) =>
            sample.workflowBusinessStatus?.code ===
              initialWorkflowStatus.code &&
            sample.stepBusinessStatus?.code === initialStepStatus.code,
        )
      )
        sampleStatuses.push({
          workflowBusinessStatus: initialWorkflowStatus,
          stepBusinessStatus: initialStepStatus,
        });
      const assignmentExamples =
        presentation.filters.some((filter) => filter.field === 'assignment') ||
        presentation.tiles.some((tile) => tile.statusScope === 'assignment');
      const exampleAssignees = (
        status: string,
      ): (number | null | undefined)[] => {
        if (!assignmentExamples) return [undefined];
        const progress = preparation
          ? status
          : status === 'approved'
            ? 'completed'
            : status === 'not_requested'
              ? 'pending'
              : 'in_progress';
        return progress === 'in_progress'
          ? [null, this.syntheticActorUserId, 2]
          : progress === 'assigned'
            ? [this.syntheticActorUserId, 2]
            : [null];
      };
      const rows =
        (view.workflowStatusCodes != null && !workflowStatusOptions.length) ||
        (view.stepStatusCodes != null && !stepStatusOptions.length)
          ? []
          : baseRows.flatMap((row) =>
              sampleStatuses.flatMap((statuses) =>
                exampleAssignees(row.statusCode).map((syntheticAssignee) => ({
                  ...row,
                  ...statuses,
                  syntheticAssignee,
                })),
              ),
            );
      this.dashboard = {
        dashboard: {
          identity: 'preview',
          kind: reviewWork
            ? 'review-work'
            : hearingDay
              ? 'hearing-day'
              : preparation
                ? 'preparation'
                : 'review',
          definitionVersionId: 1,
          workflowCode: this.document.workflowCode,
          version: this.document.definitionVersionCode,
          nodeId: this.node.id,
          stepLabel: this.node.displayName || this.node.stepCode || '',
          dashboardId: view.id,
          label: view.label,
          route: '',
          selectedForNewWork: false,
          hasRetainedWork: false,
          resultSchemaHash: view.view.outputSchemaHash,
        },
        columns: view.columns,
        ...(hearingDay
          ? {
              day: sampleDay,
              timeZoneId: sampleZone,
              posture: null,
              postureOptions: [{ code: 'scheduled', label: 'Scheduled' }],
              postureCounts: [{ statusCode: 'scheduled', count: rows.length }],
            }
          : {}),
        assignment: initial('assignment') || null,
        assignmentOptions: hasDashboardAssignment(this.document)
          ? dashboardAssignmentChoices
          : [],
        presentation,
        page: 1,
        pageSize: 25,
        totalCount: rows.length,
        rows: rows.map((row, i) => ({
          ...row,
          ...(hearingDay
            ? {
                hearingCycleId: i + 1,
                proceedings:
                  Number.isFinite(sampleStart.getTime()) &&
                  Number.isFinite(sampleEnd.getTime())
                    ? [
                        {
                          scheduledProceedingId: i + 1,
                          proceedingNumber: `EXAMPLE-${i + 1}`,
                          postureCode: 'scheduled',
                          scheduledStartUtc: sampleStart.toISOString(),
                          scheduledEndUtc: sampleEnd.toISOString(),
                          timeZoneId: sampleZone,
                          sessionContextCode:
                            i % 3 === 0
                              ? ('none' as const)
                              : i % 3 === 1
                                ? ('single' as const)
                                : ('multiple' as const),
                          sittings: Array.from(
                            { length: i % 3 },
                            (_, index) => ({
                              id: i * 10 + index + 1,
                              sittingKey: `example-${i + 1}-${index + 1}`,
                              sequence: index + 1,
                              statusCode:
                                index === 0 ? 'scheduled' : 'adjourned',
                            }),
                          ),
                        },
                      ]
                    : [],
              }
            : {}),
          stepProgress: preparation
            ? row.statusCode
            : row.statusCode === 'approved'
              ? 'completed'
              : row.statusCode === 'not_requested'
                ? 'pending'
                : 'in_progress',
          workflowInstanceId: i + 1,
          publicReference: `DEMO-${String(i + 1).padStart(6, '0')}`,
          ...(this.document?.subjectTypeCode === 'hearing_cycle' ? {
            subjectName: ['Jordan Rivera', 'Alex Morgan', 'Taylor Brooks'][i % 3],
            subjectIdentifier: `A${String(i + 1).padStart(5, '0')}`,
            subjectLocation: i % 2 ? 'Folsom State Prison' : 'California State Prison, Sacramento',
            hearingSummary: {
              hearingType: i % 2 ? 'subsequent' : 'initial',
              hearingStatus: i % 2 ? 'closed' : 'open',
              scheduledHearingUtc: i % 2 ? null : '2026-11-19T17:00:00Z',
              eligibilityDate: '2026-10-12',
              hearingDueDate: '2026-12-01',
              nextHearingDeadline: i % 2 ? '2029-11-19' : null,
              projectedReleaseDate: null,
            },
          } : {}),
          occurrenceId: i + 1,
          stepCode: this.node!.stepCode ?? undefined,
          assigneeUserId:
            row.syntheticAssignee !== undefined
              ? row.syntheticAssignee
              : ['assigned', 'in_progress'].includes(row.statusCode)
                ? this.syntheticActorUserId
                : null,
          assigneeDisplayName:
            row.syntheticAssignee !== undefined
              ? row.syntheticAssignee == null
                ? null
                : row.syntheticAssignee === this.syntheticActorUserId
                  ? 'Example staff member (you)'
                  : 'Another example staff member'
              : ['assigned', 'in_progress'].includes(row.statusCode)
                ? 'Example staff member'
                : null,
          occurrenceNumber: 1,
          isCurrent: [
            'not_requested',
            'pending',
            'assigned',
            'in_progress',
          ].includes(row.statusCode),
          workflowStatus: ['approved', 'completed'].includes(row.statusCode)
            ? 'completed'
            : 'in_progress',
          updatedUtc: '2026-10-04T16:00:00Z',
          route: '',
          ...(reviewWork ? this.reviewWorkExample(row.statusCode, i) : {}),
        })),
        historical: !reviewWork && initial('history') === 'history',
        search: initial('search') || null,
        status: initial('status') || null,
        defaultStatus: String(view.defaults['status'] || '') || null,
        ...(reviewWork ? { defaultStatuses: declaredReviewStatuses } : {}),
        workflowStatus: initial('workflowStatus') || null,
        stepStatus: initial('stepStatus') || null,
        stepProgress: initial('stepProgress') || null,
        stepProgressOptions: progressChoices,
        stepProgressCounts: progressChoices.map((choice) => ({
          statusCode: choice.code,
          count: rows.filter((row) =>
            preparation
              ? row.statusCode === choice.code
              : (row.statusCode === 'approved'
                  ? 'completed'
                  : row.statusCode === 'not_requested'
                    ? 'pending'
                    : 'in_progress') === choice.code,
          ).length,
        })),
        defaultWorkflowStatusCodes: view.workflowStatusCodes || null,
        defaultStepStatusCodes: view.stepStatusCodes || null,
        workflowStatusOptions,
        stepStatusOptions,
        workflowStatusCounts: workflowStatusOptions.map(
          ({ code: statusCode }) => ({
            statusCode,
            count: rows.filter(
              (row) => row.workflowBusinessStatus?.code === statusCode,
            ).length,
          }),
        ),
        stepStatusCounts: stepStatusOptions.map(({ code: statusCode }) => ({
          statusCode,
          count: rows.filter(
            (row) => row.stepBusinessStatus?.code === statusCode,
          ).length,
        })),
        statusOptions: statusOptions.filter((status) =>
          reviewWork
            ? declaredReviewStatuses.includes(status)
            : !view.defaults['status'] || status === view.defaults['status'],
        ),
        statusCounts: [...new Set(rows.map((row) => row.statusCode))].map(
          (statusCode) => ({
            statusCode,
            count: rows.filter((row) => row.statusCode === statusCode).length,
          }),
        ),
      };
    }
    if (view && !this.dashboard) {
      this.dashboardNotice = registeredView
        ? `A dashboard preview is not available for ${registeredView.label}.`
        : 'No matching installed view is available to preview this saved dashboard.';
    }
    const interaction = declaration.detail.mainInteraction;
    const commands = declaration.behavior.commands;
    if (
      interaction &&
      !commands.some((c) =>
        sameCapabilityReference(c.capability, interaction.command),
      )
    ) {
      this.detailNotice =
        'The selected form does not match an action on this step. Select a matching form before previewing it.';
    }
    const actions: WorkspaceAction[] = commands.map((c) => {
      const registered =
        this.componentCatalog?.commands.filter((item) =>
          sameCapabilityReference(item.capability, c.capability),
        ) ?? [];
      const metadata = registered.length === 1 ? registered[0] : null;
      const form = metadata?.form
        ? {
            ...metadata.form,
            configuration: c.formConfiguration,
            ...(metadata.supportsCaptureForm
              ? { captureForm: c.captureForm }
              : {}),
          }
        : null;
      const blockReasons = form
        ? []
        : [
            {
              code: 'preview.form_unavailable',
              message:
                'No matching installed form is available to preview this action.',
            },
          ];
      return {
        command: c.capability,
        label: metadata?.label ?? c.capability.key.code,
        decisionRecordId: 101,
        enabled: !!form,
        form,
        blockReasons,
        outcomes: c.outcomes.map((o) => ({
          ...o,
          enabled: !!form,
          blockReasons,
          requirements: c.requirements.map((code) => ({
            code,
            state: 'preview',
            label:
              metadata?.requirements.find((item) => item.code === code)
                ?.label ?? code,
          })),
        })),
        explanation: {
          provider: 'preview',
          version: 1,
          status: 'synthetic',
          message:
            'Example only. Live requirements are evaluated by the server.',
        },
      };
    });
    this.workspaceActions = actions;
    this.workspaceLedger = {
      workflowInstanceId: 1,
      workflowCode: this.document.workflowCode,
      workflowLabel: this.document.workflowCode,
      versionLabel: this.document.definitionVersionCode,
      statusCode: this.sampleVisitCompleted ? 'completed' : 'in_progress',
      statusLabel: this.sampleVisitCompleted ? 'Completed' : 'In progress',
      currentStepCode: this.node.stepCode,
      currentStepLabel: this.node.displayName || this.node.stepCode,
      counts: {
        totalSteps: 1,
        completedSteps: this.sampleVisitCompleted ? 1 : 0,
        activeSteps: this.sampleVisitCompleted ? 0 : 1,
        notStartedSteps: 0,
        attentionSteps: 0,
      },
      steps: [
        {
          id: 'preview',
          stepInstanceId: 1,
          stepCode: this.node.stepCode || this.node.id,
          stepLabel:
            this.node.displayName || this.node.stepCode || this.node.id,
          displayOrder: 0,
          occurrence: this.sampleVisitNumber,
          statusCode: this.sampleVisitCompleted ? 'completed' : 'in_progress',
          statusLabel: this.sampleVisitCompleted ? 'Completed' : 'In progress',
          isCurrent: !this.sampleVisitCompleted,
          items: [],
          evidenceAvailability: 'unavailable',
        },
      ],
      evidenceAvailability: 'unavailable',
    };
    this.workspace = {
      definitionVersionId: 1,
      selectedOccurrenceId: 1,
      nodeId: this.node.id,
      stepLabel: this.node.displayName || this.node.stepCode || this.node.id,
      isCurrent: !this.sampleVisitCompleted,
      detail: declaration.detail,
      context: {
        version: 1,
        domain: {},
        workflow: {
          workflowInstanceId: 1,
          definitionVersionId: 1,
          stepOccurrenceId: 1,
          nodeId: this.node.id,
          occurrenceNumber: this.sampleVisitNumber,
        },
      },
      subject: { type: 'Synthetic example', id: 1 },
      execution: {
        workflowInstanceId: 1,
        publicReference: 'DEMO-000001',
        workflowCode: this.document.workflowCode,
        definitionVersionCode: this.document.definitionVersionCode,
        canonicalContentHash: 'preview',
        configurationVersionId: 1,
        statusCode: this.sampleVisitCompleted ? 'completed' : 'in_progress',
        currentStepCode: this.node.stepCode,
        graphExecutionEnabled: true,
        activeOccurrenceId: this.sampleVisitCompleted ? undefined : 1,
        eTag: 'preview',
        stepETag: 'preview',
        occurrences: [
          {
            id: 1,
            definitionNodeId: this.node.id,
            stepCode: this.node.stepCode || this.node.id,
            occurrenceNumber: this.sampleVisitNumber,
            statusCode: this.sampleVisitCompleted ? 'completed' : 'in_progress',
          },
        ],
        availableActions: {
          occurrenceId: 1,
          stepETag: 'preview',
          evaluatedUtc: 'Synthetic preview',
          blockReasons: [],
          actions,
        },
      },
    };
    this.sampleJourney = detailSampleJourney(
      pageDocument,
      pageNode,
      this.componentCatalog,
      actions,
    );
    this.workspaceChecklist = requirementPreviewChecklist(
      declaration.behavior.completionRequirements ?? [],
      this.sampleJourney,
    );
  }
}
